import { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Table } from '../../components/ui/Table';
import { syncTimestampColumns } from '../../components/ui/SyncTimestamps';
import { Badge } from '../../components/ui/Badge';
import { FilterBar } from '../../components/ui/FilterBar';
import { Modal } from '../../components/ui/Modal';
import api from '../../services/api';

const PER_PAGE = 10;
const STATUSES = ['preparation', 'loading', 'departed', 'arrived', 'delivered', 'cancelled'];
const STATUS_VARIANT = {
  preparation: 'preparing', loading: 'preparing', departed: 'transit',
  arrived: 'transit', delivered: 'delivered', cancelled: 'info',
};

function displayDate(value, locale) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-GB');
}

function Detail({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 pb-2 text-[13px]">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 text-right font-medium">{value || '—'}</span>
    </div>
  );
}

export default function OrdersPage() {
  const { t, i18n } = useTranslation();
  const { user, hasPermission, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const canView = hasPermission('view_orders');
  const isAdmin = user?.company?.type === 'ADMIN';
  const [orders, setOrders] = useState([]);
  const [companies, setCompanies] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!authLoading && !canView) navigate('/dashboard', { replace: true });
  }, [authLoading, canView, navigate]);

  useEffect(() => {
    if (authLoading || !canView) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    api.get('/orders')
      .then(({ data }) => { if (active) setOrders(Array.isArray(data) ? data : []); })
      .catch((err) => { if (active) setError(err.message || t('common.error_occurred')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [authLoading, canView, reload, t]);

  useEffect(() => {
    if (!isAdmin || !canView) return undefined;
    let active = true;
    api.post('/companies/get-all?skip=0&limit=500', { type: 'CLIENT' })
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : (data?.data ?? data?.companies ?? []);
        if (active) setCompanies(Object.fromEntries(rows.map((company) => [String(company._id), company.name])));
      })
      .catch(() => {});
    return () => { active = false; };
  }, [isAdmin, canView, reload]);

  const visible = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    return orders.filter((order) => {
      if (status !== 'all' && order.status !== status) return false;
      if (!term) return true;
      const client = companies[String(order.clientId)] || (isAdmin ? '' : user?.company?.name);
      return [order.reference, order.productName, client, order.origin?.city,
        order.origin?.portCode, order.destination?.portCode,
        order.destination?.finalDestinationCode].some((value) =>
        String(value ?? '').toLocaleLowerCase().includes(term));
    });
  }, [orders, search, status, companies, isAdmin, user]);

  const clientName = (order) => companies[String(order.clientId)] || (isAdmin ? '—' : user?.company?.name || '—');
  const columns = [
    { key: 'reference', label: t('orders.reference'), render: (value) => <span className="font-mono font-medium text-primary">{value || '—'}</span> },
    { key: 'clientId', label: t('orders.client'), render: (_, row) => clientName(row) },
    { key: 'productName', label: t('orders.product'), render: (value) => value || '—' },
    { key: 'route', label: t('orders.route'), render: (_, row) => `${row.origin?.city || row.origin?.portCode || '—'} → ${row.destination?.finalDestinationCode || row.destination?.portCode || '—'}` },
    { key: 'etd', label: t('orders.etd'), render: (value) => displayDate(value, i18n.language) },
    { key: 'totalQuantityTons', label: t('orders.quantity'), align: 'right', render: (value) => value == null ? '—' : value.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-GB') },
    { key: 'status', label: t('orders.status'), render: (value) => <Badge variant={STATUS_VARIANT[value] || 'info'}>{t(`orders.statuses.${value}`, { defaultValue: value || '—' })}</Badge> },
    ...syncTimestampColumns(t, i18n.language),
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">{t('orders.title')}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{t('orders.subtitle')}</p>
        </div>
        <button onClick={() => setReload((n) => n + 1)} disabled={loading} className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />{t('common.refresh')}
        </button>
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex items-center justify-between text-[13px] text-rose-700">
        <span>{error}</span><button onClick={() => setReload((n) => n + 1)} className="font-medium underline">{t('common.retry')}</button>
      </div>}

      <FilterBar
        search={search}
        onSearch={(event) => { setSearch(event.target.value); setPage(1); }}
        searchPlaceholder={t('orders.search')}
        actions={<select aria-label={t('orders.status')} value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }} className="h-9 px-2 bg-white border border-slate-200 rounded-lg text-[12.5px] text-slate-700">
          <option value="all">{t('orders.all_statuses')}</option>
          {STATUSES.map((value) => <option key={value} value={value}>{t(`orders.statuses.${value}`)}</option>)}
        </select>}
      />

      <Table
        columns={columns}
        rows={visible.slice((page - 1) * PER_PAGE, page * PER_PAGE)}
        rowKey="_id"
        loading={loading}
        onRowClick={setSelected}
        emptyState={<p className="px-4 py-8 text-center text-[13px] text-slate-500">{t('orders.empty')}</p>}
        page={page}
        total={visible.length}
        perPage={PER_PAGE}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />

      <Modal open={Boolean(selected)} onClose={() => setSelected(null)} title={selected?.reference || t('orders.title')}>
        {selected && <div className="space-y-2">
          <Detail label={t('orders.client')} value={clientName(selected)} />
          <Detail label={t('orders.product')} value={selected.productName} />
          <Detail label={t('orders.status')} value={t(`orders.statuses.${selected.status}`, { defaultValue: selected.status })} />
          <Detail label={t('orders.mode')} value={selected.mode} />
          <Detail label={t('orders.quantity')} value={selected.totalQuantityTons == null ? null : String(selected.totalQuantityTons)} />
          <Detail label={t('orders.origin')} value={[selected.origin?.city, selected.origin?.portCode].filter(Boolean).join(' · ')} />
          <Detail label={t('orders.destination')} value={selected.destination?.finalDestinationCode || selected.destination?.portCode} />
          <Detail label={t('orders.etd')} value={displayDate(selected.etd, i18n.language)} />
          <Detail label={t('orders.atd')} value={displayDate(selected.atd, i18n.language)} />
          <Detail label={t('orders.shipments')} value={selected.shipmentsCount == null ? null : String(selected.shipmentsCount)} />
          <Detail label={t('orders.incoterm')} value={selected.incoterm} />
        </div>}
      </Modal>
    </div>
  );
}
